import React from 'react';

const CheckCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LightningIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const MetricCard = ({ value, label, tone }) => {
  const toneClasses = tone === 'good'
    ? 'bg-green-500/10 border-green-500/30 text-green-400'
    : 'bg-amber-500/10 border-amber-500/30 text-amber-400';
  return (
    <div className={`border rounded p-3 text-center ${toneClasses}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs uppercase font-medium opacity-90">{label}</p>
    </div>
  );
};

const ResultRow = ({ ok, primary, detail }) => (
  <div className={`px-3 py-2 text-xs border-b border-gray-700 last:border-b-0 flex items-center gap-2 ${ok ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
    <span className={ok ? 'text-green-400' : 'text-red-400'}>{ok ? <CheckCircleIcon /> : <XCircleIcon />}</span>
    <span className="font-mono text-gray-300">{primary}</span>
    <span className={`ml-auto truncate max-w-[260px] ${ok ? 'text-green-400' : 'text-red-400'}`} title={detail}>{detail}</span>
  </div>
);

const CostStructureModal = ({
  isOpen,
  onClose,
  validation,
  taskResults,
  costTypeResults,
  busyAction,           // null | 'tasks' | 'costtypes'
  onCreateTasks,
  onCreateCostTypes,
}) => {
  if (!isOpen || !validation) return null;

  const allClear = validation.missingTasks.length === 0 && validation.missingCostTypes.length === 0;
  const isBusy = busyAction !== null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh] border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-amber-500/10 rounded-t-lg flex items-center gap-2">
          <LightningIcon className="w-5 h-5 text-amber-400" />
          <h3 className="font-bold text-white">Cost Structure Validation</h3>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <MetricCard value={validation.found} label="Found" tone="good" />
            <MetricCard value={validation.missingTasks.length} label="Missing Tasks" tone={validation.missingTasks.length === 0 ? 'good' : 'warn'} />
            <MetricCard value={validation.missingCostTypes.length} label="Missing Cost Types" tone={validation.missingCostTypes.length === 0 ? 'good' : 'warn'} />
          </div>

          {allClear && !taskResults && !costTypeResults && (
            <div className="bg-green-500/10 border border-green-500/30 rounded p-3 mb-4 flex items-center gap-2">
              <span className="text-green-400"><CheckCircleIcon /></span>
              <span className="text-sm font-semibold text-green-300">
                All {validation.totalRequired} cost structure combinations already exist.
              </span>
            </div>
          )}

          {validation.missingTasks.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-400 uppercase">Missing Tasks ({validation.missingTasks.length})</p>
                <button
                  onClick={onCreateTasks}
                  disabled={isBusy}
                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 rounded text-xs font-bold flex items-center gap-1"
                >
                  {busyAction === 'tasks' ? <><SpinnerIcon /> Creating...</> : <>⚡ Create All Tasks</>}
                </button>
              </div>
              <div className="border border-gray-600 rounded max-h-32 overflow-y-auto bg-gray-900">
                {validation.missingTasks.slice(0, 100).map((t, idx) => (
                  <div key={idx} className="px-3 py-1.5 text-xs border-b border-gray-700 last:border-b-0 font-mono text-gray-300">
                    {t.project} / {t.costCode}
                  </div>
                ))}
                {validation.missingTasks.length > 100 && (
                  <div className="px-3 py-1.5 text-xs italic text-gray-500">... and {validation.missingTasks.length - 100} more</div>
                )}
              </div>
            </div>
          )}

          {taskResults && taskResults.filter(r => r.status === 'error').length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-bold text-gray-400 uppercase mb-2">
                Task Errors ({taskResults.filter(r => r.status === 'error').length})
                <span className="ml-2 text-gray-500 normal-case font-normal">
                  · {taskResults.filter(r => r.status === 'created').length} created successfully
                </span>
              </p>
              <div className="border border-gray-600 rounded max-h-40 overflow-y-auto bg-gray-900">
                {taskResults.filter(r => r.status === 'error').map((r, idx) => (
                  <ResultRow
                    key={idx}
                    ok={false}
                    primary={`${r.project} / ${r.costCode}`}
                    detail={r.error}
                  />
                ))}
              </div>
            </div>
          )}

          {validation.missingCostTypes.length > 0 && (
            <div className="mb-4">
              {validation.missingTasks.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-2 text-xs text-amber-300">
                  Note: {validation.missingTasks.length} task(s) still missing. Cost types for those tasks will fail to create.
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-400 uppercase">Missing Cost Types ({validation.missingCostTypes.length})</p>
                <button
                  onClick={onCreateCostTypes}
                  disabled={isBusy}
                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 rounded text-xs font-bold flex items-center gap-1"
                >
                  {busyAction === 'costtypes' ? <><SpinnerIcon /> Creating...</> : <>⚡ Create All Cost Types</>}
                </button>
              </div>
              <div className="border border-gray-600 rounded max-h-32 overflow-y-auto bg-gray-900">
                {validation.missingCostTypes.slice(0, 100).map((ct, idx) => (
                  <div key={idx} className="px-3 py-1.5 text-xs border-b border-gray-700 last:border-b-0 font-mono text-gray-300">
                    {ct.project} / {ct.costCode} / {ct.costType}
                  </div>
                ))}
                {validation.missingCostTypes.length > 100 && (
                  <div className="px-3 py-1.5 text-xs italic text-gray-500">... and {validation.missingCostTypes.length - 100} more</div>
                )}
              </div>
            </div>
          )}

          {costTypeResults && costTypeResults.filter(r => r.status === 'error').length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase mb-2">
                Cost Type Errors ({costTypeResults.filter(r => r.status === 'error').length})
                <span className="ml-2 text-gray-500 normal-case font-normal">
                  · {costTypeResults.filter(r => r.status === 'created').length} created successfully
                </span>
              </p>
              <div className="border border-gray-600 rounded max-h-40 overflow-y-auto bg-gray-900">
                {costTypeResults.filter(r => r.status === 'error').map((r, idx) => (
                  <ResultRow
                    key={idx}
                    ok={false}
                    primary={`${r.project} / ${r.costCode} / ${r.costType}`}
                    detail={r.error}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-end gap-2 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={isBusy}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded text-gray-300 font-medium hover:bg-gray-600 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostStructureModal;